/* eslint-disable @typescript-eslint/no-explicit-any */
import _ from 'lodash';

import { BadgeListProps, Layout, Size } from '@wix/members-badge-lib';

import { BADGE_LAYOUT, BADGE_SIZE } from '../../constants/badges';
import Wix from '../../services/wix-sdk-polyfill';
import defaultValues, { fontsDefaults } from '../../constants/wix-params/default-values';
import keys from '../../constants/wix-params/keys';
import { getSiteLocale, getAppComponent } from '../site/selectors';
import { PROFILE } from '../../constants/app-components';
import { getMemberBadges } from '../../selectors/badges';
import { getCurrentMemberId, getViewedMemberId } from '../members/selectors';
import { getGlobalSettings } from '../../selectors/settings-selectors';

const badgeLayoutMap: { [key in BADGE_LAYOUT]: Layout } = {
  [BADGE_LAYOUT.ICON_ONLY]: 'icon-only',
  [BADGE_LAYOUT.NAME_AND_ICON]: 'name-icon',
  [BADGE_LAYOUT.NAME_ONLY]: 'name-only',
};

const badgeSizeMap: { [key in BADGE_SIZE]: Size } = {
  [BADGE_SIZE.SMALL]: 'small',
  [BADGE_SIZE.MEDIUM]: 'medium',
  [BADGE_SIZE.LARGE]: 'large',
};

const getBadgeLayoutString = (layout: BADGE_LAYOUT) => badgeLayoutMap[layout || 0];
const getBadgeSizeString = (size: BADGE_SIZE) => badgeSizeMap[size || 0];

const getBadgesIds = (state: any) => state.badgesDefinitions.ids;
const getBadgesMap = (state: any) => state.badgesDefinitions.byId;

export const getBadges = (state: any) => {
  const badgesMap = getBadgesMap(state);
  return getBadgesIds(state).map((id: string) => badgesMap[id]);
};

export const getBadgeDefinition = (state: any, id: string) => state.badgesDefinitions.byId[id];

export const getBadgesMembersCountMap = (state: any): Record<string, number> => state.badgesDefinitions.memberCountById;

export const getIsEditingBadges = (state: any) => {
  return state.badgesDefinitions && state.badgesDefinitions.isEditingBadges;
};

export const getBadgesFont = (state: any) => {
  const { siteTextPresets = {} } = getGlobalSettings(state);
  const defaultFont = (siteTextPresets && siteTextPresets[fontsDefaults[keys.BADGE_FONT].themeName]) || {};
  const defaultFontFamilyValue = defaultFont.fontFamily;
  const savedSettings = state.settings?.component?.styleParams ?? {};
  return _.get(savedSettings, `fonts.${keys.BADGE_FONT}.family`) || defaultFontFamilyValue;
};

export const getMemberBadgesSettings = (state: any, memberId: string): BadgeListProps => {
  const currentUserId = getCurrentMemberId(state);
  const viewedMemberId = getViewedMemberId(state);
  const savedSettings = state.settings?.component?.styleParams ?? {};
  const align = getAppComponent(state) === PROFILE ? 'center' : 'left';
  const useTextRemainder = false; // Let's use the number everywhere for now
  const maxRows = 1;
  const userBadges = getMemberBadges(state, memberId);
  const showBadgeBackground = _.get(savedSettings, `booleans.${keys.SHOW_BADGE_BACKGROUND}`);
  const borderRadius = _.get(savedSettings, `numbers.${keys.BADGE_CORNER_RADIUS}`);
  const layout = _.get(savedSettings, `numbers.${keys.BADGE_LAYOUT}`);
  const size = _.get(savedSettings, `numbers.${keys.BADGE_SIZE}`);
  const remainderColor = _.get(
    savedSettings,
    `colors.${keys.BUTTON_OPACITY_AND_COLOR}.value`,
    defaultValues[keys.BUTTON_OPACITY_AND_COLOR],
  );

  // Preview if viewed user has no badges (in PW) or if viewed user is the same as current (Members list) just for user itself
  const shouldPreviewBadges =
    getIsEditingBadges(state) &&
    userBadges.length === 0 &&
    (currentUserId !== viewedMemberId || viewedMemberId === memberId);

  return {
    badges: shouldPreviewBadges ? getBadges(state) : userBadges,
    layout: getBadgeLayoutString(layout === undefined ? defaultValues[keys.BADGE_LAYOUT] : layout),
    size: getBadgeSizeString(size === undefined ? defaultValues[keys.BADGE_SIZE] : size),
    borderRadius: borderRadius === undefined ? defaultValues[keys.BADGE_CORNER_RADIUS] : borderRadius,
    font: getBadgesFont(state),
    background: showBadgeBackground === undefined ? defaultValues[keys.SHOW_BADGE_BACKGROUND] : showBadgeBackground,
    align,
    maxRows,
    language: getSiteLocale(state),
    useTextRemainder,
    siteColors: (state.settings && state.settings.global.siteColors) || Wix.Styles.getSiteColors(),
    remainderColor,
  };
};
